import React from "react";
import {BOV_LOGO} from "../bov-templates";

/**
 * Shared chrome for every BOV page.
 *
 * Three variants, matching how the template itself is put together:
 *  - `bleed`  a single full-bleed image (pages 01 and 06)
 *  - `fixed`  a fixed composition positioned with Abs (pages 05, 07, 08, 09, 10)
 *  - default  flowing content under an eyebrow, title and rule (pages 02, 03, 04)
 *
 * The display title is upright dark followed by italic green ending in a period
 * ("Property Name.", "Value Analysis."); pass those as `title` and `titleAccent`.
 */

/**
 * The display title shrinks to fit beside the address, the same way the cover's property
 * name does.
 *
 * It is set in `white-space: pre`, so it never wraps - a long one just runs on. The header is
 * a flex row of title and address, so what actually happened was that the title pushed the
 * address into a column two or three characters wide: "2111 Houston Hwy, Victoria, TX, 77901"
 * stacked vertically down the right edge of page 02.
 *
 * `TITLE_ROOM` is what the title may occupy: the full 960px content width, because the
 * address moved up onto the eyebrow's line on 2026-09-18 and no longer stands beside it. It
 * was 680 - the same width less an allowance for the longest address and a gutter - which
 * shrank titles that now have the room to print at full size. Held as a constant because
 * there is no layout to measure at render; the deck is built for print and never laid out
 * on screen.
 *
 * `TITLE_ADVANCE` is the average glyph advance as a fraction of the font size for the display
 * serif, rounded up so the estimate errs small rather than overflowing.
 */
const TITLE_SIZE_MAX = 44.8;
const TITLE_SIZE_MIN = 26;
const TITLE_ADVANCE = 0.46;
const TITLE_ROOM = 960;

function titleFontSize(title, titleAccent) {
    const chars = `${title || ''}${titleAccent || ''}`.trim().length;
    if (!chars) return TITLE_SIZE_MAX;
    const fitted = TITLE_ROOM / (chars * TITLE_ADVANCE);
    return Math.max(TITLE_SIZE_MIN, Math.min(TITLE_SIZE_MAX, Math.floor(fitted * 2) / 2));
}
export default function PageFrame(props) {
    const {
        eyebrow,
        title,
        titleAccent,
        headerRight,
        pageNo,
        totalPages,
        bleed,
        fixed,
        dark,
        rule = true,
        children,
    } = props;

    // `--flow` and `--fixed` are the two that draw the mark below, and the stylesheet hangs
    // the foot line off them for that reason: the page number and any agent lines share the
    // mark's band on exactly the pages that have a mark to share it with.
    const classes = ['bov-page'];
    if (bleed) classes.push('bov-page--bleed');
    else if (fixed) classes.push('bov-page--fixed');
    else classes.push('bov-page--flow');
    if (dark) classes.push('bov-page--dark');

    return (
        <div className={classes.join(' ')}>
            {bleed || fixed ? null : (
                <div className='bov-page__header'>
                    {/* The address shares the eyebrow's line rather than standing beside the
                        title, which leaves the title the whole content width. */}
                    <div className='bov-page__eyebrowRow'>
                        {eyebrow ? <div className='bov-page__eyebrow'>{eyebrow}</div> : null}
                        {headerRight ? <div className='bov-page__headerRight'>{headerRight}</div> : null}
                    </div>
                    {title || titleAccent ? (
                        <h1
                            className='bov-page__title'
                            style={{fontSize: `${titleFontSize(title, titleAccent)}px`}}
                        >
                            {title}
                            {titleAccent ? <em>{titleAccent}</em> : null}
                        </h1>
                    ) : null}
                </div>
            )}

            {bleed || fixed || !rule ? null : <div className='bov-page__rule'/>}

            {bleed || fixed ? children : <div className='bov-page__body'>{children}</div>}

            {/* Bleed pages (01, 06) carry the mark inside their own artwork. */}
            {bleed ? null : <img className='bov-page__logo' src={BOV_LOGO} alt='STRIVE'/>}
            {/* Light text only on a dark ground. Being full-bleed says nothing about
                that: page 01's artwork is dark, page 06's is white.

                The number sits on the STRIVE mark's line, y=786..800, on every page - drawn
                there below on the flowing and fixed pages, baked into the artwork at the
                same height on Global Reach, the one bleed page that prints one. There was a
                `--aboveStrip` modifier here that lifted it clear of the cover's contact
                strip; the cover and the back cover print no number at all now, so nothing
                reached it and it has gone. `dark` itself is still live, on page 08. */}
            <div className={[
                'bov-page__pageno',
                dark ? 'bov-page__pageno--onDark' : '',
            ].filter(Boolean).join(' ')}>
                {formatPageNo(pageNo, totalPages)}
            </div>
        </div>
    );
}

function formatPageNo(pageNo, totalPages) {
    if (!pageNo) return null;
    const pad = n => String(n).padStart(2, '0');
    return `${pad(pageNo)} / ${pad(totalPages)}`;
}
